import { isSessionActive } from "./authStorage";
import { supabase, isSupabaseConfigured, assertSupabaseAllowed } from "./supabase";
import { buildDashboardView } from "./mapSupabaseToDashboard";

async function fetchSingle(table, id = "default") {
  const { data, error } = await supabase
    .from(table)
    .select("*")
    .eq("id", id)
    .maybeSingle();
  return { data, error, count: data ? 1 : 0 };
}

async function fetchList(table, queryFn) {
  const { data, error, count } = await queryFn(supabase.from(table));
  const rows = data || [];
  return { data: rows, error, count: count ?? rows.length };
}

export async function fetchPmaDashboardPayload() {
  if (!isSessionActive()) {
    return {
      ok: false,
      error: new Error("Unauthorized — login required before accessing Supabase data."),
      raw: null,
      view: null,
      tableDiagnostics: [],
    };
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      ok: false,
      error: new Error("Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY."),
      raw: null,
      view: null,
      tableDiagnostics: [],
    };
  }

  try {
    assertSupabaseAllowed();
  } catch (err) {
    return {
      ok: false,
      error: err,
      raw: null,
      view: null,
      tableDiagnostics: [],
    };
  }

  const [
    dailyRes,
    configRes,
    decisionsRes,
    positionsRes,
    analysisRes,
    articlesRes,
    xPostsRes,
  ] = await Promise.all([
    fetchSingle("daily_state"),
    fetchSingle("trading_config"),
    fetchList("decision_log", (q) =>
      q.select("*", { count: "exact" }).order("timestamp", { ascending: false }).limit(50),
    ),
    fetchList("active_positions", (q) =>
      q.select("*", { count: "exact" }).order("updated_at", { ascending: false }),
    ),
    fetchList("scheduled_analysis", (q) =>
      q.select("*", { count: "exact" }).order("priority_rank", { ascending: true, nullsFirst: false }),
    ),
    fetchList("articles", (q) =>
      q
        .select("*", { count: "exact" })
        .eq("is_irrelevant", false)
        .eq("is_duplicate", false)
        .order("published_time", { ascending: false, nullsFirst: false })
        .limit(15),
    ),
    fetchList("x_posts", (q) =>
      q.select("*", { count: "exact" }).order("scraped_at", { ascending: false }).limit(15),
    ),
  ]);

  const tableDiagnostics = [
    { table: "daily_state", count: dailyRes.count, error: dailyRes.error?.message },
    { table: "trading_config", count: configRes.count, error: configRes.error?.message },
    { table: "decision_log", count: decisionsRes.count, error: decisionsRes.error?.message },
    { table: "active_positions", count: positionsRes.count, error: positionsRes.error?.message },
    { table: "scheduled_analysis", count: analysisRes.count, error: analysisRes.error?.message },
    { table: "articles", count: articlesRes.count, error: articlesRes.error?.message },
    { table: "x_posts", count: xPostsRes.count, error: xPostsRes.error?.message },
  ];

  const errors = tableDiagnostics.map((d) => d.error).filter(Boolean);

  const raw = {
    dailyState: dailyRes.data,
    tradingConfig: configRes.data,
    decisions: decisionsRes.data || [],
    positions: positionsRes.data || [],
    analysis: analysisRes.data || [],
    articles: articlesRes.data || [],
    xPosts: xPostsRes.data || [],
  };

  const view = buildDashboardView(raw);
  const hasData =
    raw.dailyState ||
    raw.tradingConfig ||
    raw.decisions.length > 0 ||
    raw.positions.length > 0 ||
    raw.analysis.length > 0 ||
    raw.articles.length > 0 ||
    raw.xPosts.length > 0;

  if (errors.length > 0 && !hasData) {
    return {
      ok: false,
      error: errors[0],
      raw,
      view,
      errors,
      tableDiagnostics,
    };
  }

  return {
    ok: true,
    error: errors[0] || null,
    raw,
    view,
    errors,
    tableDiagnostics,
    hasData,
  };
}

/** @deprecated use fetchPmaDashboardPayload */
export async function fetchTradingDashboardPayload() {
  const result = await fetchPmaDashboardPayload();
  if (!result.ok) {
    return { ok: false, error: result.error, stats: null, log: null, report: null };
  }
  return {
    ok: true,
    error: result.error,
    stats: result.view?.stats,
    log: result.view?.log,
    report: result.view?.report,
  };
}
